import React, {useEffect, useRef} from 'react';
import Logo from '../Logo';
import {ArrowLeftIcon} from '../icons';

interface Props {
    /** Порядковый номер шага в мастере (1..totalSteps) — числитель прогресса. */
    order: number;
    totalSteps: number;
    title: string;
    subtitle: string;
    /** Подпись primary-кнопки: «Начать» / «Далее» / «Готово». */
    nextLabel: string;
    /** Можно ли пропустить этот шаг (приветствие и финал — нельзя). */
    skippable: boolean;
    /** Показывать ли «Назад» (на первом шаге — нет). */
    canGoBack: boolean;
    /** Показывать ли «Пропустить настройку» (на финальном шаге — нет). */
    canSkipAll: boolean;
    saving: boolean;
    /** Текст ошибки последнего действия; `null` — ошибки нет. */
    error: string | null;
    onNext: () => void;
    onBack: () => void;
    onSkipStep: () => void;
    onSkipAll: () => void;
    children: React.ReactNode;
}

/**
 * Оболочка мастера онбординга: прогресс, заголовок шага, слот контента и подвал с навигацией
 * (CTHS-216 AC-1/AC-2/AC-12/AC-13).
 *
 * Доступность: прогресс — `role="progressbar"` с числовыми значениями; заголовок шага получает
 * фокус при каждой смене шага, поэтому чтение с клавиатуры начинается с нового экрана, а не
 * с начала документа; ошибка — `role="alert"`. Одна primary-кнопка на регион: «Далее»;
 * «Назад», «Пропустить шаг» и «Пропустить настройку» — вторичные.
 */
const WizardShell: React.FC<Props> = ({
    order, totalSteps, title, subtitle, nextLabel, skippable, canGoBack, canSkipAll,
    saving, error, onNext, onBack, onSkipStep, onSkipAll, children,
}) => {
    const headingRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        headingRef.current?.focus();
    }, [order]);

    const percent = totalSteps > 0 ? Math.round((order / totalSteps) * 100) : 0;

    return (
        <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex flex-col" data-testid="onboarding-wizard">
            <header className="w-full max-w-2xl mx-auto px-4 pt-6 pb-2 flex items-center justify-between gap-4">
                <Logo/>
                <span className="text-sm text-gray-500 dark:text-gray-400" data-testid="onboarding-progress-label">
                    Шаг {order} из {totalSteps}
                </span>
            </header>

            <div className="w-full max-w-2xl mx-auto px-4">
                <div
                    className="h-1.5 bg-gray-200 dark:bg-ink-700 rounded-full overflow-hidden"
                    role="progressbar"
                    aria-label="Прогресс настройки"
                    aria-valuemin={1}
                    aria-valuemax={totalSteps}
                    aria-valuenow={order}
                    data-testid="onboarding-progress"
                >
                    {/* Заполнение — transform, а не ширина: анимируем только композитные свойства. */}
                    <div
                        className="h-full w-full bg-brand-500 rounded-full origin-left transition-transform duration-300 ease-out"
                        style={{transform: `scaleX(${percent / 100})`}}
                        data-testid="onboarding-progress-fill"
                    />
                </div>
            </div>

            <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-6">
                <div key={order} className="animate-fade-in space-y-5">
                    <div className="space-y-2">
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className="text-2xl font-bold text-gray-900 dark:text-gray-100 outline-none"
                            data-testid="onboarding-step-title"
                        >
                            {title}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                    </div>

                    <div className="card p-4 sm:p-6">{children}</div>

                    {error && (
                        <p
                            role="alert"
                            className="text-sm text-rose-600 dark:text-rose-400"
                            data-testid="onboarding-error"
                        >
                            {error}
                        </p>
                    )}
                </div>
            </main>

            <footer className="w-full border-t border-gray-200 dark:border-white/10 bg-white dark:bg-ink-900">
                {/* На узком экране действия шага идут первыми, служебные — под ними: до кнопки
                    «Далее» не нужно дотягиваться через весь подвал. */}
                <div className="w-full max-w-2xl mx-auto px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2 order-2 sm:order-1">
                        {canGoBack && (
                            <button
                                type="button"
                                onClick={onBack}
                                disabled={saving}
                                className="btn-secondary min-h-[44px]"
                                data-testid="onboarding-back"
                            >
                                <ArrowLeftIcon className="w-4 h-4"/>
                                Назад
                            </button>
                        )}
                        {canSkipAll && (
                            <button
                                type="button"
                                onClick={onSkipAll}
                                disabled={saving}
                                className="min-h-[44px] px-3 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
                                data-testid="onboarding-skip-all"
                            >
                                Пропустить настройку
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 order-1 sm:order-2">
                        {skippable && (
                            <button
                                type="button"
                                onClick={onSkipStep}
                                disabled={saving}
                                className="btn-secondary min-h-[44px] flex-1 sm:flex-none"
                                data-testid="onboarding-skip-step"
                            >
                                Пропустить шаг
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onNext}
                            disabled={saving}
                            className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors flex-1 sm:flex-none"
                            data-testid="onboarding-next"
                        >
                            {saving ? 'Сохранение…' : nextLabel}
                        </button>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default WizardShell;
